import React, { useState, useEffect } from 'react';
import { Card, Spinner } from 'react-bootstrap';
import { FaTimes } from 'react-icons/fa';
import { 
  RiHashtag, 
  RiCalendarCheckLine, 
  RiCalendarEventLine, 
  RiTimeLine, 
  RiCarLine, 
  RiUserLine, 
  RiPhoneLine, 
  RiMapPinLine,
  RiSpeedLine, 
  RiSpeedUpLine, 
  RiSettings4Line 
} from 'react-icons/ri';
import '../styles/VehicleTripDetails.css';
import { GetFromApi } from '../utils/api';
import socketService from '../utils/socket';

const VehicleTripDetails = ({ vehicle, onClose }) => {
  const [tripData, setTripData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (vehicle) {
      fetchTripDetails();
      
      // Subscribe to real-time updates
      socketService.connect();
      socketService.on('vehicle:location', handleRealTimeUpdate);
    }

    return () => {
      socketService.off('vehicle:location');
    };
  }, [vehicle]);

  const handleRealTimeUpdate = (data) => {
    if (tripData && data.vehicleId === vehicle.id) {
      setTripData(prevData => ({
        ...prevData,
        averageSpeed: `${data.speed} km/h`, // Using current speed for demo, normally would re-calc
        engineStatus: data.speed > 0 ? 'ON' : 'OFF',
        // Update other real-time fields as needed
        endLocation: `${data.lat.toFixed(4)}, ${data.lng.toFixed(4)}` // Show live co-ords as end location while active
      }));
    }
  };

  const fetchTripDetails = async () => {
    try {
      setLoading(true);
      const today = new Date();
      const startDate = new Date(today);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);

      // Fetch travel report for summary data
      const travelRes = await GetFromApi(`/reports/travel?vehicle_id=${vehicle.id}&start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`);
      
      // Fetch ignition report for stops/departures simulation
      const ignitionRes = await GetFromApi(`/reports/ignition?vehicle_id=${vehicle.id}&start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`);

      if (travelRes.success) {
        const summary = travelRes.data.summary || {};
        const locs = travelRes.data.locations || [];
        const startLoc = locs.length > 0 ? locs[0] : null;
        const endLoc = locs.length > 0 ? locs[locs.length - 1] : null;

        // Process ignition data
        const events = ignitionRes.success ? ignitionRes.data : [];
        const stops = events.filter(e => e.event === 'Ignition OFF').slice(0, 2).map(e => ({
          time: new Date(e.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          duration: '15 mins',
          location: e.address || 'Unknown Location'
        }));
        
        const departures = events.filter(e => e.event === 'Ignition ON').slice(0, 2).map(e => ({
          time: new Date(e.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          location: e.address || 'Unknown Location'
        }));

        const realData = {
          tripId: `TRP-${vehicle.id}-${Date.now().toString().slice(-4)}`,
          startTime: summary.startTime ? new Date(summary.startTime).toLocaleString() : 'N/A',
          endTime: summary.endTime ? new Date(summary.endTime).toLocaleString() : 'In Progress',
          duration: calculateDuration(summary.startTime, summary.endTime),
          totalDistance: `${summary.totalDistance || 0} KM`,
          driverId: `DRV-${vehicle.id}`,
          driverName: vehicle.driver_name || vehicle.driver || 'Unassigned',
          mobileNo: vehicle.driver_mobile || '+91 98765 43210',
          startLocation: startLoc ? (startLoc.address || `${startLoc.lat}, ${startLoc.lng}`) : 'N/A',
          endLocation: endLoc ? (endLoc.address || `${endLoc.lat}, ${endLoc.lng}`) : 'N/A',
          averageSpeed: `${Math.round(summary.avgSpeed || (vehicle.speed || 0))} km/h`,
          maxSpeed: `${Math.round(summary.maxSpeed || (vehicle.speed || 0))} km/h`,
          engineStatus: vehicle.speed > 0 ? 'ON' : 'OFF',
          fuelConsumed: `${(parseFloat(summary.totalDistance || 0) / 10).toFixed(1)} Litres`,
          fuelStatus: Math.max(0, Math.round(100 - (parseFloat(summary.totalDistance || 0) / 5))),
          fuelRemaining: `${Math.max(0, Math.round(80 - (parseFloat(summary.totalDistance || 0) / 10)))}L / 80L`,
          departures: departures.length ? departures : (summary.startTime ? [
            { time: new Date(summary.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), location: startLoc?.address || 'Start Point' }
          ] : []),
          stops: stops.length ? stops : []
        };
        setTripData(realData);
      }
    } catch (error) {
      console.error("Error fetching trip details:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateDuration = (start, end) => {
    if (!start) return '0 min';
    const s = new Date(start);
    const e = end ? new Date(end) : new Date();
    const diffMs = e - s;
    const diffHrs = Math.floor(diffMs / 3600000);
    const diffMins = Math.round(((diffMs % 3600000) / 60000));
    return `${diffHrs} hours ${diffMins} minutes`;
  };

  if (!vehicle) return null;

  if (loading) {
    return (
      <div className="vehicle-trip-details-overlay">
        <div className="trip-details-container d-flex justify-content-center align-items-center" style={{ backgroundColor: 'white', opacity: 0.9 }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="vehicle-trip-details-overlay">
      <div className="trip-details-container">
        <div className="trip-details-header">
          <span className="header-title">Vehicle Trip Details - {vehicle.number}</span>
          <button className="close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {tripData && (
          <div className="trip-details-content">
            <div className="details-column">
              {/* Trip Summary Card */}
              <Card className="detail-card trip-summary-card">
                <div className="card-header-custom">
                  <span>Trip Summary</span>
                </div>
                <div className="card-body-custom">
                  <div className="detail-row">
                    <RiHashtag className="detail-icon" />
                    <div className="detail-text">
                      <span className="detail-label">Trip ID : </span>
                      <span className="detail-value">{tripData.tripId}</span>
                    </div>
                  </div>

                  <div className="detail-row">
                    <RiCalendarCheckLine className="detail-icon" />
                    <div className="detail-text">
                      <span className="detail-label">Start Time & Date : </span>
                      <span className="detail-value">{tripData.startTime}</span>
                    </div>
                  </div>

                  <div className="detail-row">
                    <RiCalendarEventLine className="detail-icon" />
                    <div className="detail-text">
                      <span className="detail-label">End Time & Date : </span>
                      <span className="detail-value">{tripData.endTime}</span>
                    </div>
                  </div>

                  <div className="detail-row">
                    <RiTimeLine className="detail-icon" />
                    <div className="detail-text">
                      <span className="detail-label">Trip Duration : </span>
                      <span className="detail-value">{tripData.duration}</span>
                    </div>
                  </div>

                  <div className="detail-row">
                    <RiCarLine className="detail-icon" />
                    <div className="detail-text">
                      <span className="detail-label">Total Distance Covered : </span>
                      <span className="detail-value">{tripData.totalDistance}</span>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Driver Info Card */}
              <Card className="detail-card">
                <div className="card-header-custom">
                  <span>Driver Info</span>
                </div>
                <div className="card-body-custom">
                  <div className="detail-row">
                    <RiHashtag className="detail-icon" />
                    <div className="detail-text">
                      <span className="detail-label">Driver ID : </span>
                      <span className="detail-value">{tripData.driverId}</span>
                    </div>
                  </div>

                  <div className="detail-row">
                    <RiUserLine className="detail-icon" />
                    <div className="detail-text">
                      <span className="detail-label">Driver Name : </span>
                      <span className="detail-value">{tripData.driverName}</span>
                    </div>
                  </div>

                  <div className="detail-row">
                    <RiPhoneLine className="detail-icon" />
                    <div className="detail-text">
                      <span className="detail-label">Mobile No. : </span>
                      <span className="detail-value">{tripData.mobileNo}</span>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            <div className="details-column">
              {/* Start & End Location Card */}
              <Card className="detail-card">
                <div className="card-header-custom">
                  <span>Start & End Location</span>
                </div>
                <div className="card-body-custom">
                  <div className="location-item">
                    <RiMapPinLine className="detail-icon start-location-icon" />
                    <div className="location-text">
                      <div className="location-label">Start Location :</div>
                      <div className="location-address">{tripData.startLocation}</div>
                    </div>
                  </div>

                  <div className="location-item">
                    <RiMapPinLine className="detail-icon end-location-icon" />
                    <div className="location-text">
                      <div className="location-label">End Location :</div>
                      <div className="location-address">{tripData.endLocation}</div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Speed & Status Card */}
              <Card className="detail-card">
                <div className="card-header-custom">
                  <span>Speed & Status</span>
                </div>
                <div className="card-body-custom">
                  <div className="detail-row">
                    <RiSpeedLine className="detail-icon" />
                    <div className="detail-text">
                      <span className="detail-label">Average Speed : </span>
                      <span className="detail-value">{tripData.averageSpeed}</span>
                    </div>
                  </div>

                  <div className="detail-row">
                    <RiSpeedUpLine className="detail-icon" />
                    <div className="detail-text">
                      <span className="detail-label">Maximum Speed : </span>
                      <span className="detail-value">{tripData.maxSpeed}</span>
                    </div>
                  </div>

                  <div className="detail-row">
                    <RiSettings4Line className="detail-icon" />
                    <div className="detail-text">
                      <span className="detail-label">Engine Status : </span>
                      <span className="detail-value">{tripData.engineStatus}</span>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            <div className="details-column">
              {/* Fuel Card */}
              <Card className="detail-card">
                <div className="card-header-custom">
                  <span>Fuel Information</span>
                </div>
                <div className="card-body-custom">
                  <div className="fuel-consumed-row">
                    <span className="fuel-label">Fuel Consumed</span>
                    <span className="fuel-value">{tripData.fuelConsumed}</span>
                  </div>

                  <div className="fuel-progress-container">
                    <div className="fuel-progress-bg">
                      <div 
                        className="fuel-progress-bar" 
                        style={{ width: `${tripData.fuelStatus}%` }}
                      >
                        <div className="fuel-progress-indicator"></div>
                      </div>
                    </div>
                    <div className="fuel-status-text">
                      Fuel Status: <span className="fuel-percent">{tripData.fuelStatus}%</span> Remaining <span className="fuel-percent">{tripData.fuelRemaining}</span>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Departure & Stop Card */}
              <Card className="detail-card departure-stop-card">
                <div className="card-header-custom">
                  <span>Departure & Stop</span>
                </div>
                <div className="card-body-custom">
                  <div className="timeline-container">
                    <div className="timeline-border"></div>
                    
                    {tripData.departures && tripData.departures.map((dep, idx) => (
                      <React.Fragment key={`dep-${idx}`}>
                        <div className="timeline-item">
                          <RiMapPinLine className="timeline-icon departure-icon" />
                          <div className="timeline-content">
                            <div className="timeline-header">
                              <span className="timeline-label">Departure</span>
                              <div className="timeline-meta">
                                <svg className="time-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                  <path d="M6 11C3.23857 11 1 8.7614 1 6C1 3.23857 3.23857 1 6 1C8.7614 1 11 3.23857 11 6C11 8.7614 8.7614 11 6 11ZM6 10C8.20915 10 10 8.20915 10 6C10 3.79086 8.20915 2 6 2C3.79086 2 2 3.79086 2 6C2 8.20915 3.79086 10 6 10ZM6.5 6H8.5V7H5.5V3.5H6.5V6Z" fill="#888F9E"/>
                                </svg>
                                <span className="timeline-time">{dep.time}</span>
                              </div>
                            </div>
                            <div className="timeline-location">{dep.location}</div>
                          </div>
                        </div>

                        {tripData.stops && tripData.stops[idx] && (
                          <div className="timeline-item">
                            <RiMapPinLine className="timeline-icon stop-icon" />
                            <div className="timeline-content">
                              <div className="timeline-header">
                                <span className="timeline-label"><strong>Stop</strong> {tripData.stops[idx].duration}</span>
                                <div className="timeline-meta">
                                  <svg className="time-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path d="M6 11C3.23857 11 1 8.7614 1 6C1 3.23857 3.23857 1 6 1C8.7614 1 11 3.23857 11 6C11 8.7614 8.7614 11 6 11ZM6 10C8.20915 10 10 8.20915 10 6C10 3.79086 8.20915 2 6 2C3.79086 2 2 3.79086 2 6C2 8.20915 3.79086 10 6 10ZM6.5 6H8.5V7H5.5V3.5H6.5V6Z" fill="#888F9E"/>
                                  </svg>
                                  <span className="timeline-time">{tripData.stops[idx].time}</span>
                                </div>
                              </div>
                              <div className="timeline-location">{tripData.stops[idx].location}</div>
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleTripDetails;
